import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Loader2, AlertTriangle, Home } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { useAuth } from '@/context/AuthContext';
import { buildRedirectUri, consumeStoredState } from '@/lib/googleOAuth';

/**
 * Handles the redirect from Google's OAuth consent screen.
 * Reads ?code & ?state from the query string, verifies CSRF state matches what
 * we stored before redirect, then exchanges the code via POST /api/auth/google.
 */
export default function AuthCallbackPage() {
  const navigate = useNavigate();
  const { googleAuth } = useAuth();
  const [error, setError] = useState(null);
  const processedRef = useRef(false);  // StrictMode-safe one-shot guard
  const [params] = useSearchParams();

  useEffect(() => {
    if (processedRef.current) return;
    processedRef.current = true;

    const run = async () => {
      const code = params.get('code');
      const state = params.get('state');
      const errParam = params.get('error');

      if (errParam) {
        setError(errParam === 'access_denied'
          ? 'Sign-in was cancelled.'
          : `Google reported: ${errParam}`);
        return;
      }
      if (!code || !state) {
        setError('Missing authorization code or state — please try signing in again.');
        return;
      }
      const expectedState = consumeStoredState();
      if (!expectedState || expectedState !== state) {
        setError('Security check failed (state mismatch). Please try signing in again.');
        return;
      }

      try {
        const u = await googleAuth(code, buildRedirectUri());
        if (u.status === 'pending') {
          toast.info('Account created! Awaiting admin approval.');
        } else {
          toast.success(`Welcome, ${u.name}!`);
        }
        navigate('/', { replace: true });
      } catch (err) {
        const detail = err?.response?.data?.detail || err?.message || 'Google sign-in failed.';
        setError(detail);
      }
    };
    run();
  }, [params, googleAuth, navigate]);

  return (
    <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center px-4">
      <Card className="max-w-md w-full p-8 text-center" data-testid="auth-callback-card">
        {error ? (
          <>
            <AlertTriangle className="h-10 w-10 text-[#D4AF37] mx-auto mb-4" />
            <h2 className="text-lg font-serif text-[#14213D] mb-2">Sign-in didn&rsquo;t complete</h2>
            <p className="text-sm text-[#5A6577] mb-6 break-words">{error}</p>
            <div className="flex gap-3 justify-center">
              <Button variant="outline" onClick={() => navigate('/')} data-testid="callback-home-btn">
                <Home className="h-4 w-4 mr-2" /> Home
              </Button>
              <Button onClick={() => navigate('/login')} data-testid="callback-retry-btn">
                Try again
              </Button>
            </div>
          </>
        ) : (
          <>
            <Loader2 className="h-10 w-10 animate-spin text-[#14213D] mx-auto mb-4" />
            <h2 className="text-lg font-serif text-[#14213D]">Signing you in&hellip;</h2>
            <p className="text-sm text-[#5A6577] mt-1">Verifying your Google account</p>
          </>
        )}
      </Card>
    </div>
  );
}
